import {
  VoxProductCatalogItem,
  VoxRoiCostConfigData,
  VoxRoiUsageResponse,
  VoxRoiProductUsageItem,
  VoxRoiAfterLine,
  VoxRoiBeforeProvider,
  VoxRoiResult,
  CostScheduleEntry,
  CostGapWarning,
  UnmappedUsageGroup,
  BeforeProviderEntry,
  PricingState,
} from '../models/voxRoi';

/** Normalize a date value (string or Date from the convertDates interceptor) to YYYY-MM-DD. */
const toDateString = (value: string | Date): string => {
  if (value instanceof Date) return value.toISOString().substring(0, 10);
  return value.substring(0, 10);
};

/** Find the cost entry covering a given date, or undefined if none (missing cost). */
const findEffectiveCost = (schedule: CostScheduleEntry[] | undefined, date: string): number | undefined => {
  if (!schedule || schedule.length === 0) return undefined;
  for (const entry of schedule) {
    if (date >= entry.effectiveFrom && (entry.effectiveTo === null || date <= entry.effectiveTo)) {
      return entry.cost ?? undefined;
    }
  }
  return undefined;
};

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** True only for a real calendar date in YYYY-MM-DD form (rejects e.g. 2026-02-30). */
const isValidDate = (value: string): boolean => {
  if (!DATE_PATTERN.test(value)) return false;
  const time = Date.parse(`${value}T00:00:00Z`);
  if (Number.isNaN(time)) return false;
  return new Date(time).toISOString().substring(0, 10) === value;
};

const nextDay = (value: string): string => {
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().substring(0, 10);
};

/**
 * G7/G8: Validate a product's cost schedule. Rejects invalid or reversed dates, missing or
 * negative costs, overlaps, and gaps; a single open-ended period is allowed only as the last
 * entry. Returns a human-readable error, or null when the schedule is valid.
 */
export const validateSchedule = (schedule: CostScheduleEntry[]): string | null => {
  if (schedule.length === 0) return null;

  for (const entry of schedule) {
    if (!isValidDate(entry.effectiveFrom)) {
      return `Invalid start date: ${entry.effectiveFrom || '(blank)'}`;
    }
    if (entry.effectiveTo !== null) {
      if (!isValidDate(entry.effectiveTo)) {
        return `Invalid end date: ${entry.effectiveTo || '(blank)'}`;
      }
      if (entry.effectiveTo < entry.effectiveFrom) {
        return `End date ${entry.effectiveTo} is before start date ${entry.effectiveFrom}`;
      }
    }
    if (entry.cost === null) {
      return `Missing cost for the period starting ${entry.effectiveFrom}`;
    }
    if (!Number.isFinite(entry.cost) || entry.cost < 0) {
      return `Cost for the period starting ${entry.effectiveFrom} must be zero or greater`;
    }
  }

  const sorted = [...schedule].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    if (prev.effectiveTo === null) {
      return `The open-ended period starting ${prev.effectiveFrom} must be the last entry`;
    }
    if (curr.effectiveFrom <= prev.effectiveTo) {
      return `Overlapping periods: ${prev.effectiveFrom}–${prev.effectiveTo} and ${curr.effectiveFrom}–${curr.effectiveTo ?? 'open'}`;
    }
    if (curr.effectiveFrom !== nextDay(prev.effectiveTo)) {
      return `Gap between ${prev.effectiveTo} and ${curr.effectiveFrom}; periods must be contiguous`;
    }
  }
  return null;
};

/** Map gap/overlap schedule errors to the approved user-facing string. */
export const scheduleErrorMessage = (error: string | null): string | null => {
  if (!error) return null;
  if (error.includes('Gap') || error.includes('Overlapping')) {
    return 'fix the cost dates: no gaps or overlaps';
  }
  if (error.includes('Missing cost')) {
    return 'enter a valid amount';
  }
  return error;
};

/** Stable signature for an unmapped usage row: the service-provided mapping fields, no PII/costs. */
const unmappedSignature = (row: VoxRoiProductUsageItem): string =>
  [row.vendor, row.transactionName, row.reportType, row.dataType || 'no-data', row.isSmartOutreach ? 'smart-outreach' : 'standard'].join('|');

/** Derive a line's pricing state from its priced vs unpriced order counts. */
const linePricingState = (pricedCount: number, unpricedCount: number): PricingState => {
  if (unpricedCount === 0) return 'complete';
  if (pricedCount === 0) return 'missing';
  return 'partial';
};

interface AfterLineAccumulator extends VoxRoiAfterLine {
  gapDates: Set<string>;
}

/** Sort product keys by catalog sortOrder, then productKey. Shared by report and editor. */
export const compareByCatalogOrder = (
  catalogByKey: Map<string, { sortOrder: number }>,
) => (a: string, b: string): number => {
  const sortA = catalogByKey.get(a)?.sortOrder ?? Number.MAX_SAFE_INTEGER;
  const sortB = catalogByKey.get(b)?.sortOrder ?? Number.MAX_SAFE_INTEGER;
  if (sortA !== sortB) return sortA - sortB;
  return a.localeCompare(b);
};

/** Sort catalog items by sortOrder, then productKey. */
export const sortCatalogItems = <T extends { productKey: string; sortOrder: number }>(items: T[]): T[] =>
  [...items].sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.productKey.localeCompare(b.productKey);
  });

export const computeRoi = ({
  config,
  catalog,
  usage,
}: {
  config: VoxRoiCostConfigData;
  catalog: VoxProductCatalogItem[];
  usage: VoxRoiUsageResponse;
}): VoxRoiResult => {
  const { totalUnitCount, productUsage } = usage;
  const { costs, beforeProviders: beforeProviderConfig } = config;

  const catalogByKey = new Map(catalog.map((item) => [item.productKey, item]));

  // --- BEFORE (flat per-provider cost, no date-effective repricing) ---
  const beforeKeys = Object.keys(beforeProviderConfig).sort(compareByCatalogOrder(catalogByKey));
  const providerCount = beforeKeys.length;

  const beforeCostPerLoan = beforeKeys.reduce((sum, key) => {
    const entry: BeforeProviderEntry = beforeProviderConfig[key];
    return sum + entry.ordersPerLoan * entry.cost;
  }, 0);
  const beforeTotalSpend = beforeCostPerLoan * totalUnitCount;

  const beforeProviders: VoxRoiBeforeProvider[] = beforeKeys.map((key) => {
    const catalogItem = catalogByKey.get(key);
    const entry = beforeProviderConfig[key];
    return {
      productKey: key,
      vendor: catalogItem?.vendor ?? 'Unknown',
      productDisplayName: catalogItem?.productDisplayName ?? key,
      ordersPerLoan: entry.ordersPerLoan,
      costPerOrder: entry.cost,
    };
  });

  // --- AFTER (per-day, date-effective). Missing prices are tracked, never coerced to $0. ---
  const unmappedGroupMap = new Map<string, UnmappedUsageGroup>();
  const afterLineMap = new Map<string, AfterLineAccumulator>();
  let afterKnownSpend = 0;
  let pricedOrderCount = 0;
  let totalAfterOrderCount = 0;

  for (const usageItem of productUsage) {
    totalAfterOrderCount += usageItem.successCount;

    const { productKey, isMapped, successCount, date } = usageItem;
    const catalogProduct = isMapped ? catalogByKey.get(productKey) : undefined;

    if (!catalogProduct) {
      const signature = unmappedSignature(usageItem);
      const existingGroup = unmappedGroupMap.get(signature);
      if (existingGroup) {
        existingGroup.successCount += successCount;
      } else {
        unmappedGroupMap.set(signature, {
          signature,
          vendor: usageItem.vendor,
          transactionName: usageItem.transactionName,
          reportType: usageItem.reportType,
          dataType: usageItem.dataType,
          isSmartOutreach: usageItem.isSmartOutreach,
          successCount,
        });
      }
      continue;
    }

    const dateStr = toDateString(date);
    const unitCost = findEffectiveCost(costs[productKey], dateStr);
    const isPriced = unitCost !== undefined;
    const knownSpend = isPriced ? unitCost * successCount : 0;
    afterKnownSpend += knownSpend;
    if (isPriced) {
      pricedOrderCount += successCount;
    }

    const existing = afterLineMap.get(productKey);
    if (existing) {
      existing.successCount += successCount;
      existing.knownSpend += knownSpend;
      if (isPriced) {
        existing.pricedCount += successCount;
      } else {
        existing.unpricedCount += successCount;
        existing.gapDates.add(dateStr);
      }
    } else {
      afterLineMap.set(productKey, {
        productKey,
        vendor: catalogProduct.vendor,
        productDisplayName: catalogProduct.productDisplayName,
        sortOrder: catalogProduct.sortOrder,
        successCount,
        pricedCount: isPriced ? successCount : 0,
        unpricedCount: isPriced ? 0 : successCount,
        knownSpend,
        pricingState: 'complete',
        gapDates: isPriced ? new Set() : new Set([dateStr]),
      });
    }
  }

  const afterLines: VoxRoiAfterLine[] = [];
  const productsMissingCost: CostGapWarning[] = [];
  let unpricedOrderCount = 0;

  for (const accumulator of afterLineMap.values()) {
    const { gapDates, ...line } = accumulator;
    line.pricingState = linePricingState(line.pricedCount, line.unpricedCount);
    afterLines.push(line);
    unpricedOrderCount += line.unpricedCount;

    if (line.unpricedCount > 0) {
      productsMissingCost.push({
        productKey: line.productKey,
        vendor: line.vendor,
        productDisplayName: line.productDisplayName,
        affectedOrderCount: line.unpricedCount,
        dates: Array.from(gapDates).sort(),
      });
    }
  }

  afterLines.sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.productKey.localeCompare(b.productKey);
  });

  productsMissingCost.sort((a, b) => {
    const sortA = catalogByKey.get(a.productKey)?.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const sortB = catalogByKey.get(b.productKey)?.sortOrder ?? Number.MAX_SAFE_INTEGER;
    if (sortA !== sortB) return sortA - sortB;
    return a.productKey.localeCompare(b.productKey);
  });

  const afterAvgCostPerLoan = totalUnitCount > 0 ? afterKnownSpend / totalUnitCount : 0;
  const afterPricingState: PricingState =
    unpricedOrderCount === 0
      ? 'complete'
      : afterLines.some((line) => line.pricedCount > 0)
        ? 'partial'
        : 'missing';

  const unmappedUsage = Array.from(unmappedGroupMap.values()).sort((a, b) => a.signature.localeCompare(b.signature));
  const unmappedOrderCount = unmappedUsage.reduce((sum, group) => sum + group.successCount, 0);

  // totalAfterOrderCount already includes unmapped rows (counted before the unmapped continue).
  // Any successful After activity with zero priced orders → Unavailable, never a known $0 / 100%.
  const totalSuccessfulActivity = totalAfterOrderCount;
  const afterFullyUnpriced = totalSuccessfulActivity > 0 && pricedOrderCount === 0;
  const hasIncompletePricing = unpricedOrderCount > 0 || unmappedUsage.length > 0;

  // --- SAVINGS (uses known After spend; an upper bound when pricing is incomplete) ---
  const savingsTotal = afterFullyUnpriced ? null : beforeTotalSpend - afterKnownSpend;
  const savingsPct =
    afterFullyUnpriced ? null : beforeTotalSpend > 0 ? (beforeTotalSpend - afterKnownSpend) / beforeTotalSpend : null;
  const savingsPerLoan = afterFullyUnpriced ? null : beforeCostPerLoan - afterAvgCostPerLoan;

  return {
    totalUnitCount,
    before: { costPerLoan: beforeCostPerLoan, totalSpend: beforeTotalSpend, providerCount, providers: beforeProviders },
    after: {
      knownSpend: afterKnownSpend,
      avgCostPerLoan: afterAvgCostPerLoan,
      lines: afterLines,
      pricingState: afterPricingState,
      unpricedOrderCount,
      pricedOrderCount,
      afterFullyUnpriced,
    },
    savings: { total: savingsTotal, pct: savingsPct, perLoan: savingsPerLoan },
    warnings: {
      unmappedUsage,
      unmappedOrderCount,
      productsMissingCost,
      hasIncompletePricing,
      afterFullyUnpriced,
    },
  };
};
